import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePhases } from '../../contexts/PhaseContext';
import {
  PitchSubmissionDoc,
  emptyPitch,
  getPitch,
  savePitch,
} from '../../services/pitch-submission-service';
import { getMyStartup } from '../../services/startup-service';
import PitchForm from './PitchForm';

const InitialPitch: React.FC = () => {
  const { loading: phaseLoading } = usePhases();
  const [pitch, setPitch] = useState<PitchSubmissionDoc>(emptyPitch('initial'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([getPitch('initial'), getMyStartup()])
      .then(([p, s]) => {
        const base = p || emptyPitch('initial');
        setPitch(s ? {
          ...base,
          startupName:  base.startupName  || s.name             || '',
          website:      base.website      || s.website          || '',
          problem:      base.problem      || s.problemStatement || '',
          solution:     base.solution     || s.solution         || '',
          targetMarket: base.targetMarket || s.targetMarket     || '',
          industry:     base.industry     || s.industry         || '',
          revenueModel: base.revenueModel || s.revenueModel     || '',
          traction:     base.traction     || s.traction         || '',
        } : base);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Phase 0 is permanently read-only once submitted — it is a preserved record.
  // The Final Pitch is a separate form that pre-fills from this data.
  const readOnly = pitch.isSubmitted;

  const handleSaveDraft = async (data: PitchSubmissionDoc) => {
    setSaving(true);
    setError('');
    try {
      const saved = await savePitch('initial', { ...data, isSubmitted: false });
      setPitch(saved);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (data: PitchSubmissionDoc) => {
    setSaving(true);
    setError('');
    try {
      const saved = await savePitch('initial', { ...data, isSubmitted: true });
      setPitch(saved);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || phaseLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="section-gap max-w-4xl">
      {/* Back link */}
      <Link
        to="/app/lms/courses"
        className="inline-flex items-center gap-1.5 text-muted hover:text-text text-sm transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        Back to Learning
      </Link>

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs font-medium bg-warning/15 text-warning px-2.5 py-0.5 rounded-full">Phase 0</span>
          {pitch.isSubmitted && (
            <span className="text-xs font-medium bg-success/15 text-success px-2.5 py-0.5 rounded-full">Submitted ✓</span>
          )}
        </div>
        <h1 className="font-display text-4xl tracking-wide text-text leading-none">Initial Pitch</h1>
        <p className="font-serif-display italic text-text text-lg mt-2">
          {pitch.isSubmitted
            ? 'Submitted and preserved as a record. Your Final Pitch will open separately once all phases are complete.'
            : 'Submit before reaching the midpoint phase. All fields are optional.'}
        </p>
      </div>

      {error && (
        <p className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-4 py-3">{error}</p>
      )}

      <PitchForm
        type="initial"
        data={pitch}
        readOnly={readOnly}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        saving={saving}
      />
    </div>
  );
};

export default InitialPitch;
